import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ReactTimezoneSelect from "react-timezone-select";
import { buildTimezoneSelectStyles } from "../Workflow/timezoneSelectStyles";
import { toast } from "react-hot-toast";
import { updateBulkMessageCampaign, startBulkMessageCampaign } from "../../redux/Slice/bulkMessageCampaignSlice";

// Time slots are offered in 10-minute steps (1:00, 1:10, 1:20, ...) — matches
// the granularity the scheduled-broadcast capture/poll jobs actually run at,
// so there's no point offering finer-grained times the backend can't honor precisely.
const SCHEDULE_MINUTE_STEP = 10;

const nextScheduleSlot = () => {
  const base = dayjs().add(1, "hour").second(0).millisecond(0);
  const remainder = base.minute() % SCHEDULE_MINUTE_STEP;
  return remainder === 0 ? base : base.add(SCHEDULE_MINUTE_STEP - remainder, "minute");
};

const SendBroadcastModal = ({
  open,
  onClose,
  recipients = 0,
  campaignId,
  initialScheduledAt,
  initialTimezone,
  onSent,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDark = theme.palette.mode === "dark";

  const [deliveryTime, setDeliveryTime] = useState("schedule"); // "schedule" | "now"
  // Reopening an already-scheduled broadcast shows its current time instead
  // of a fresh default, so "update the time" starts from what's actually set.
  const [scheduledTime, setScheduledTime] = useState(() =>
    initialScheduledAt ? dayjs(initialScheduledAt) : nextScheduleSlot()
  );
  const [timezone, setTimezone] = useState(
    initialTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  );
  const [submitting, setSubmitting] = useState(false);

  const isScheduling = deliveryTime === "schedule";

  const handleTimezoneChange = (tz) => {
    setTimezone(typeof tz === "string" ? tz : tz?.value || "UTC");
  };

  const handleSubmit = async () => {
    if (!campaignId) {
      toast.error("Save the broadcast before sending.");
      return;
    }

    setSubmitting(true);
    try {
      if (isScheduling) {
        await dispatch(
          updateBulkMessageCampaign({
            campaignId,
            status: "SCHEDULED",
            scheduledAt: scheduledTime.toDate().toISOString(),
            scheduledTimezone: timezone,
          })
        ).unwrap();
        toast.success(`Broadcast scheduled for ${scheduledTime.format("MMM D, YYYY h:mm A")}.`);
      } else {
        await dispatch(startBulkMessageCampaign({ campaignId })).unwrap();
        toast.success("Broadcast is sending now.");
      }
      onSent?.();
      onClose();
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Failed to send broadcast.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Send Broadcast
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sending a total of {recipients} message{recipients === 1 ? "" : "s"} to {recipients} recipient
          {recipients === 1 ? "" : "s"}.
        </Typography>

        <Typography variant="body2" fontWeight="500" gutterBottom>
          Delivery time
        </Typography>
        <RadioGroup
          row
          value={deliveryTime}
          onChange={(e) => setDeliveryTime(e.target.value)}
          sx={{ mb: 2 }}
        >
          <FormControlLabel value="schedule" control={<Radio size="small" />} label="Schedule for later" />
          <FormControlLabel value="now" control={<Radio size="small" />} label="Send now" />
        </RadioGroup>

        {isScheduling && (
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Date & time
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  value={scheduledTime}
                  onChange={(newValue) => setScheduledTime(newValue)}
                  minDateTime={dayjs()}
                  minutesStep={SCHEDULE_MINUTE_STEP}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Time zone
              </Typography>
              <ReactTimezoneSelect
                value={timezone}
                onChange={handleTimezoneChange}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={buildTimezoneSelectStyles(isDark, {
                  menuPortalZIndex: theme.zIndex.modal + 1,
                  accentColor: theme.palette.primary.main,
                  fontSize: "0.8125rem",
                })}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || (isScheduling && (!scheduledTime || !scheduledTime.isValid()))}
        >
          {submitting ? "Please wait..." : isScheduling ? "Schedule Broadcast" : "Send Broadcast"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendBroadcastModal;
